import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle,
  BookOpen,
  Users,
  Timer,
  HelpCircle,
  FileText,
  Edit
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const Wikispaces = () => {
  const faqData = [
    {
      question: "What was Wikispaces?",
      answer: "Wikispaces was a wiki hosting service founded in 2005 that became the go-to platform for classroom wikis. It allowed teachers and students to collaboratively create and edit web pages for class projects, portfolios, and knowledge bases."
    },
    {
      question: "Why did Wikispaces shut down?",
      answer: "Wikispaces shut down in 2018 due to infrastructure costs. After a technical review, they found modernizing the aging infrastructure would be too costly. Classroom wikis closed July 31, 2018, and all services ended by January 31, 2019."
    },
    {
      question: "Is CSW a replacement for Wikispaces?",
      answer: "CSW serves different purposes but supports similar collaborative learning goals. While it's not a wiki platform, CSW offers virtual study rooms, task management, and real-time collaboration tools for student groups."
    },
    {
      question: "How many people used Wikispaces?",
      answer: "Wikispaces hosted millions of pages of content and was used by schools worldwide for class websites, student portfolios, collaborative projects, and club pages. In 2008, they gave away 100,000 ad-free wikis to K-12 teachers."
    },
    {
      question: "What happened to my Wikispaces content?",
      answer: "Wikispaces provided export tools before shutdown so users could download their content. Many migrated to Google Sites, Weebly, or other wiki platforms. Unfortunately, content not exported before the deadline was lost."
    },
    {
      question: "Where did Wikispaces users migrate to?",
      answer: "Many educators moved to Google Sites, Google Classroom, Microsoft Teams, Notion, or standalone wiki software. For collaborative study sessions, platforms like CSW offer virtual rooms for real-time teamwork."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Wikispaces Alternative - Free Collaborative Learning Platform",
    "description": "Looking for a Wikispaces alternative? Wikispaces shut down in 2018. CSW offers free virtual study rooms and collaboration tools for students and educators.",
    "url": "https://chillstudy.work/wikispaces/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Wikispaces",
      "applicationCategory": "EducationApplication",
      "operatingSystem": "Web"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Chill Study Work",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser"
    },
    "keywords": "Wikispaces alternative, Wikispaces replacement, Wikispaces shut down, classroom wiki, educational wiki platform"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1000",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Virtual study rooms",
      "Focus timer",
      "Task management",
      "Video collaboration",
      "Study communities",
      "Reminders",
      "AI-powered flashcards",
      "Progress tracking"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wikispaces Alternative - Free Collaborative Learning Platform | CSW</title>
        <meta name="description" content="Looking for a Wikispaces alternative? Wikispaces shut down in 2018. CSW offers free virtual study rooms and collaboration tools for students and educators." />
        <meta name="keywords" content="Wikispaces alternative, Wikispaces replacement, Wikispaces shut down, classroom wiki, educational wiki, collaborative learning" />
        <link rel="canonical" href="https://chillstudy.work/wikispaces/" />
        <meta property="og:title" content="Wikispaces Alternative - Free Collaborative Learning Platform" />
        <meta property="og:description" content="Wikispaces shut down in 2018. CSW offers free virtual study rooms for collaborative learning." />
        <meta property="og:url" content="https://chillstudy.work/wikispaces/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <AlertTriangle className="w-4 h-4" />
              Wikispaces shut down • July 31, 2018
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Wikispaces Alternative
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Wikispaces revolutionized classroom collaboration for 13 years before shutting down in 2018. 
              While CSW isn't a wiki, it continues the spirit of <Link to="/study-with-friends/" className="text-primary hover:underline">collaborative learning</Link> with 
              free virtual <Link to="/group-study/" className="text-primary hover:underline">study rooms</Link> and productivity tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to="/wikispaces/history/">
                  Read Wikispaces History
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-500">
                  <Edit className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">What Happened to Wikispaces?</h2>
                  <p className="text-muted-foreground">
                    Wikispaces was founded in 2005 by Adam Frey and James Byers, becoming the definitive 
                    platform for classroom wikis. Schools used it for class websites, student portfolios, 
                    and collaborative projects. After being acquired by TES Global in 2014, the platform 
                    was shut down in 2018 due to the substantial cost of modernizing aging infrastructure.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">2005</div>
                  <div className="text-sm text-muted-foreground">Founded by Frey & Byers</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">13 Years</div>
                  <div className="text-sm text-muted-foreground">Serving educators worldwide</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-destructive mb-1">July 2018</div>
                  <div className="text-sm text-muted-foreground">Classroom wikis shut down</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why CSW Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Collaborative Learning Continues with CSW</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Users className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Real-Time Collaboration</h3>
                <p className="text-muted-foreground">
                  While wikis were asynchronous, CSW offers <Link to="/study-stream/" className="text-primary hover:underline">live study rooms</Link> where 
                  students collaborate in real-time with video, chat, and shared goals.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <BookOpen className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Knowledge Building</h3>
                <p className="text-muted-foreground">
                  Create and study <Link to="/flash-cards/" className="text-primary hover:underline">flashcards</Link> and 
                  <Link to="/quizzes/" className="text-primary hover:underline"> quizzes</Link> together, building shared knowledge like wiki contributors did.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Timer className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Focused Work Sessions</h3>
                <p className="text-muted-foreground">
                  Use our <Link to="/study-timer/" className="text-primary hover:underline">Pomodoro timer</Link> to stay focused 
                  while working on group projects and study sessions.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Edit className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground">
                  Organize group projects with <Link to="/task-management/" className="text-primary hover:underline">task lists</Link> and 
                  assign who writes which part of the report.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <CheckCircle2 className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Free for Everyone</h3>
                <p className="text-muted-foreground">
                  Like Wikispaces' free education tier, CSW is completely free with unlimited access 
                  for students and educators worldwide.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Clock className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Actively Maintained</h3>
                <p className="text-muted-foreground">
                  Unlike Wikispaces' aging infrastructure, CSW is actively developed and maintained 
                  to ensure reliability.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 border border-border/50">
              <div className="flex items-center gap-3 mb-6">
                <HelpCircle className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* External References */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Learn More About Wikispaces</h2>
            <div className="space-y-3">
              <a 
                href="https://en.wikipedia.org/wiki/Wikispaces" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Wikipedia: Wikispaces</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://www.planeta.com/wikispaces/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Planeta: Wikispaces Says Farewell</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready for Collaborative Learning?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              While wikis have their place, real-time collaboration is the future. 
              Join CSW for free virtual study rooms and productivity tools.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Try CSW Free Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Wikispaces" />

      <Footer />
    </div>
  );
};

export default Wikispaces;
